import {
    Modal,
    Form,
    Select,
    Spin,
    Button,
    notification,
} from "antd";
import React, { useEffect, useState } from "react";
import {
    getUserPlan,
    updateUserPlan,
    getAllPlan,
} from "../../Services/subscription";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

const { Option } = Select;

function PlanUpdateModal({ isModalVisible, handleCancel, initialValues }) {
    const [form] = Form.useForm();
    const [billingCycle, setBillingCycle] = useState("monthly"); // default to monthly
    const queryClient = useQueryClient();

    const {
        data,
        isLoading,
    } = useQuery({
        queryKey: [`user-plan-${initialValues?.id}`],
        queryFn: () => getUserPlan(initialValues?.id),
        enabled: !!initialValues?.id && isModalVisible,
    });

    const {
        data: allPlansData,
        isLoading: isPlansLoading,
    } = useQuery({
        queryKey: ['all-plans'],
        queryFn: getAllPlan,
        enabled: isModalVisible,
    });

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                selected_plan_id: data.plan?.id,
            });
        }

        if (isModalVisible && !data) {
            form.resetFields();
        }
    }, [data, isModalVisible, form]);

    const handleFinish = async (values) => {
        if (values.selected_plan_id === data?.plan_id) {
            if(values.billing_cycle === data?.cost_type){
                notification.warning({
                    message: "No Change Detected",
                    description: "Selected plan is same as current plan.",
                });
                return;
            }
        }
        try {
            const response = await updateUserPlan(
                initialValues?.id,
                values.selected_plan_id,
                values.billing_cycle,
                data?.id
            );
            if (response) {
                if(response.status_code === 400){
                    notification.error({
                        message: "Update Failed",
                        description: response.message,
                    });
                }
                else{
                    notification.success({ message: "Plan Updated Successfully" });
                    queryClient.invalidateQueries(["users"]);
                    handleCancel();
                }
            }
        } catch (error) {
            notification.error({
                message: "Update Failed",
                description: error.message,
            });
        }
    };

    // Filter plans based on billing cycle
    const filteredPlans = allPlansData?.plans?.filter((plan) => {
        if (!plan.is_enable || plan.is_deleted || plan.is_default_plan || plan.is_upcoming_plan) return false;

        if (billingCycle === "monthly") {
            return typeof plan.monthly_cost === "number"; // allow 0 too
        } else if (billingCycle === "yearly") {
            return typeof plan.yearly_cost === "number";
        }
        return false;
    });


    return (
        <Modal
            title="Plan Update Without Payment"
            open={isModalVisible}
            footer={null}
            onCancel={handleCancel}
        >
            {isLoading || isPlansLoading ? (
                <Spin />
            ) : data ? (
                <>
                    <div style={{ marginBottom: 16 }}>
                        <p><strong>Current Plan Name:</strong> {data.name || "-"}</p>
                        <p>
                            <strong>Current Expiry Date:</strong>{" "}
                            {data.expire_datetime
                                ? dayjs(data.expire_datetime).format("YYYY-MM-DD HH:mm:ss")
                                : (data.is_default_plan ? "Nil" : "-")}
                        </p>
                        <p>
                            <strong>Purchased Type:</strong> {data.cost_type
                                ? data.cost_type.charAt(0).toUpperCase() + data.cost_type.slice(1).toLowerCase()
                                : "-"}

                        </p>
                    </div>

                    <Form form={form} layout="vertical" onFinish={handleFinish}>
                        {/* Billing Cycle Selection */}
                        <Form.Item
                            label="Billing Cycle"
                            name="billing_cycle"
                            rules={[{ required: true, message: "Please select a billing cycle" }]}
                            initialValue="monthly"
                        >
                            <Select onChange={(val) => setBillingCycle(val)}>
                                <Option value="monthly">Monthly</Option>
                                <Option value="yearly">Yearly</Option>
                            </Select>
                        </Form.Item>

                        {/* Plan Selection */}
                        <Form.Item
                            label="Select New Plan"
                            name="selected_plan_id"
                            rules={[{ required: true, message: "Please select a plan" }]}
                        >
                            <Select placeholder="Choose a plan">
                                {filteredPlans?.map((plan) => (
                                    <Option key={plan.id} value={plan.id}>
                                        {plan.name} - ${billingCycle === "monthly" ? plan.monthly_cost : plan.yearly_cost}/{billingCycle === "monthly" ? "month" : "year"}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" block>
                                Update
                            </Button>
                        </Form.Item>
                    </Form>
                </>
            ) : (
                <p style={{ color: "#888" }}>
                    No plan data available for this user.
                </p>
            )}
        </Modal>
    );
}

export default PlanUpdateModal;
